/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import * as _ from 'lodash';
import template from './folio-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {updateFormWarnings} from '../common/controller/form-submit-state';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-folio-edit-page',
  template
})
export class FolioEditPageComponent extends AdminResourceEditPageBase {
  teams: any[] = [];
  showTeams = false;
  advertiserResults: any[] = [];
  advertiserInfo = true;
  chartCurrency: any;
  canSeeReports = false;
  showingTraffic = false;
  showingLocation = false;
  perms: any = {};
  blockSubmit = false;
  activeTab: 'charts' | 'traffic' | 'location' = 'charts';

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(SearchResource) private readonly searchResource: any,
    ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true || this.perms.AX_ADVERTISER;
    this.showTeams = LocalNetworkInfo.obtain().getInfo('teamsCount') > 1 || !LocalNetworkProfile.isPureMarketplacePlatform();

    const isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.advertiserInfo = !isPureMarketplace || !!this.uiRouter.globals.params.isNew;

    this.Team.query((page: any) => {
      // [] → results flips *ngFor on the team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        const allTeams = page.results || [];
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        if (_.get(this, ['editingModel', 'team', 'id'])) {
          const hydratedTeam = _.find(allTeams, (t: any) => this.editingModel.team.id === t.id);
          if (hydratedTeam) {
            this.editingModel.team = hydratedTeam;
          }
        }
        if (allTeams.length === 1 && this.teams.length === 1 && !this.editingModel.team) {
          this.editingModel.team = allTeams[0];
        }
        this.resumeFormDirtyTracking();
      });
    });
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Advertiser'}).then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.advertiserResults = data.searchResults;
      });
    });
  };

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  selectTab(tab: FolioEditPageComponent['activeTab']): void {
    this.activeTab = tab;
    if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    }
  }

  get showAdvertiserField(): boolean {
    return this.perms.MANAGE_ADVERTISING === true || this.perms.AX_ADVERTISER === true;
  }

  onFolioCreativesAdvertiserFixed(saved: any): void {
    this.ngZone.run(() => {
      if (saved) {
        this.applySave(saved);
      }
    });
  }

  protected afterInitEditingModel(): void {
    const advertiser = advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
    if (advertiser) {
      this.editingModel.advertiser = advertiser;
    }
    this.blockSubmit = !!this.uiRouter.globals.params.readOnly;
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency']);
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj) {
      this.editingModel.advertiser = saveObj.advertiser;
    }
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency']);
  }

  protected onBeforeSubmit(): void {
    if (!_.get(this.editingModel.advertiser, 'id')) {
      this.editingModel.advertiser = null;
    }
    if (this.isNew) {
      LocalNetworkInfo.obtain().increment('folioCount');
    }
  }

  protected syncEditingModelFromExternal(data: any): void {
    if (data?.objectState !== undefined) {
      this.editingModel.objectState = data.objectState;
    }
    updateFormWarnings(this.submitState, this.model, this.LocalNetworkProfile.isPureMarketplacePlatform());
    safeDetectChanges(this.cdr);
  }
}
